import { Injectable } from '@angular/core'
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { ClientRepresentedEvent } from '@events/models/event.model'
import { WeightAttribute } from '@personas/attributes/weight.attribute'
import { PersonaFieldTypes } from '@personas/enums/persona-fields-types.enum'
import { PersonaTypes } from '@personas/enums/persona-types.enum'
import { PersonaFieldValue } from '@personas/interfaces/persona-field-value.interface'
import { PersonaField, PersonaFields } from '@personas/interfaces/persona-field.interface'
import { DEFAULT_PERSONA, Persona, PersonaCommunicationPayload, Personas, SmsCommunication, WhatsappCommunication } from '@personas/interfaces/persona.interface'
import { LandingPageOptions } from '../interfaces/persona-landing-option.interface'
import { Schedule } from '../interfaces/persona-options.interface'
import { PersonaPropertyValueChangeHooks, PersonaValueChangeHook } from './persona.value-change.hook'

export const BARCODE_COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Egyptian Blue', value: '#1434A4' },
  { name: 'Blue', value: '#0096FF' },
  { name: 'Light Blue', value: '#5BC6E8' },
  { name: 'Teal', value: '#0088ce' },
  { name: 'Peacock', value: '#008889' },
  { name: 'Burgundy', value: '#800020' },
  { name: 'Green', value: '#34B37E' },
  { name: 'Orange', value: '#FF991F' },
  { name: 'Pink', value: '#FF69B4' },
  { name: 'Purple', value: '#6454C0' },
  { name: 'Red', value: '#FF5530' },
  { name: 'Blood Orange', value: '#F26722' },
  { name: 'Science Gold', value: '#E1A537' }
]

/**
 * Persona factory service handles creating personas/ manipulating it's structure
 */
@Injectable({
  providedIn: 'root'
})
export class PersonaFactoryService {
  /**
   * Validators
   */
  minLabelLength = 2
  /**
   * Creates the service & injects it's dependencies
   * @param auth AuthService
   */
  constructor(private auth: AuthService) {}

  /**
   * Global object that contains persona properties value change hooks
   */
  private _globalValueChangesHooks: PersonaPropertyValueChangeHooks = {}
  /**
   * Attachs a persona value change hook to a certain persona property
   * Also defines if it's a multi mode or single which means a value change hook can be registered multiple times otherwise only a single instance is available
   * @param property property name
   * @param valueChangeHook PersonaValueChangeHook
   * @param valueChangeHookConfigurations value change hook configurations
   */
  public registerGlobalValueChangeHook(property: string, valueChangeHook: PersonaValueChangeHook, valueChangeHookConfigurations: { multi: boolean } = { multi: false }) {
    if (Object.prototype.hasOwnProperty.call(this._globalValueChangesHooks, property) && !!this._globalValueChangesHooks[property]) {
      if (valueChangeHookConfigurations.multi) {
        this._globalValueChangesHooks[property].push(valueChangeHook)
      } else {
        if (!this._globalValueChangesHooks[property].find((o) => o.constructor.name === valueChangeHook.constructor.name)) {
          this._globalValueChangesHooks[property].push(valueChangeHook)
        }
      }
    } else {
      this._globalValueChangesHooks[property] = [valueChangeHook]
    }
  }
  /**
   * Gets a list of the constructor names for the global persona value change hooks
   */
  public get globalValueChangesHooks(): any[] {
    return Object.keys(this._globalValueChangesHooks).map((property) => {
      return {
        property: property,
        hooks: this._globalValueChangesHooks[property].map((o) => o.constructor.name)
      }
    })
  }

  /**
   * Generates personas for an event by it's id and attaches physical id and zones ids
   * @param personas Persona[]
   * @param eventId number
   * @param physicalId number
   * @param sublocationsIds number[]
   * @returns FormArray
   */
  public generateFormArray(personas: Personas = [DEFAULT_PERSONA], eventId: number, physicalId: number, sublocationsIds: [], eventName: string): UntypedFormArray {
    const personasFormArray: UntypedFormArray = new UntypedFormArray(
      personas.map((persona, index) => this.generateFormGroup(persona, physicalId, sublocationsIds, eventId, index, personas, eventName))
    )
    Object.keys(this._globalValueChangesHooks).forEach((property) => {
      personasFormArray.controls.forEach((formGroup: UntypedFormGroup) => {
        formGroup.get(property).valueChanges.subscribe((value) => {
          this._globalValueChangesHooks[property].forEach((hook) => hook.valueChanged(personasFormArray, formGroup, formGroup.get(property), value))
        })
      })
    })
    return personasFormArray
  }

  /**
   * Generates a new landing_page_options that represents all options related to landing_page
   * It creates default values for these options
   * @returns LandingPageOptions
   */
  public generateLandingPageOptions(): LandingPageOptions {
    return {
      branding: { id: 1, text: 'Event branding', isEnabled: true },
      timing: { id: 2, text: 'Dates & Timings', isEnabled: false },
      about: { id: 3, text: 'About the event', isEnabled: false },
      downloads: { id: 4, text: 'Downloads', isEnabled: false, files: [] },
      location: { id: 5, text: 'Event location', isEnabled: false },
      form: { id: 6, text: 'Registration form', isEnabled: true, trackableMirrors: ['facebook'] },
      contacts: {
        id: 7,
        text: 'Organized by & Key contacts',
        isEnabled: false,
        contactsList: []
      }
    }
  }

  /**
   * Generates a new form group that reperesents a persona attaching zones, physical ids, index, and also applying global value change hooks
   * Also initializes the handshake factory for the persona
   * @param persona Persona
   * @param physicalId number
   * @param sublocationsIds number[]
   * @param eventId number
   * @param index number
   * @param personas Persona[]
   * @param applyGlobalHooks boolean
   * @param globalHookPersonasFormArray FormArray
   * @returns FormGroup
   */
  public generateFormGroup(
    persona = DEFAULT_PERSONA,
    physicalId: number,
    sublocationsIds: [],
    eventId: number,
    index: number,
    personas: Personas,
    eventName: string,
    applyGlobalHooks = false,
    globalHookPersonasFormArray: UntypedFormArray = null,
    landing_page_options: LandingPageOptions = null
  ): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({
      event_id: new UntypedFormControl(eventId),
      id: new UntypedFormControl(persona.id),
      slug: new UntypedFormControl(persona.slug),
      require_approval: new UntypedFormControl(persona.require_approval),
      name: new UntypedFormControl(persona.name, Validators.required),
      physical_type_id: new UntypedFormControl(physicalId),
      currency_id: new UntypedFormControl(persona.currency_id ? persona.currency_id : null),
      registration_amount: new UntypedFormControl(persona.registration_amount ? persona.registration_amount : null),
      enable_payment: new UntypedFormControl(persona.enable_payment ? persona.enable_payment : false),
      sub_locations_ids: new UntypedFormControl(persona?.checkins?.length ? persona.checkins.map((o) => o.id) : sublocationsIds ?? []),
      promo_code_ids: new UntypedFormControl(persona?.promo_codes?.map((o) => o.id) ?? []),
      promo_codes: new UntypedFormControl(persona?.promo_codes ?? []),
      weight: new UntypedFormControl(this.generateWeight(persona.weight, index, personas), Validators.required),
      maximum_registrations: new UntypedFormControl(persona.maximum_registrations),
      success_message: new UntypedFormControl(persona.success_message),
      regret_message: new UntypedFormControl(persona.regret_message),
      show_in_events: new UntypedFormControl(persona.show_in_events ?? false),
      enable_communication: new UntypedFormControl(persona.enable_communication ?? false),
      whatsapp_communication: this.generateWhatsappCommunicationFormGroup(persona.form_email?.whatsapp_communication ?? persona.whatsapp_communication),
      sms_communication: this.generateSmsCommunicationFormGroup(persona.form_email?.sms_communication ?? persona.sms_communication),
      form_elements: new UntypedFormArray(
        persona.form_elements
          .sort((a, b) => (a.weight > b.weight ? 1 : -1))
          .map((personaField: PersonaField, fieldIndex: number, personaFields: PersonaFields) => this.generatePersonaFieldFormGroup(personaField, personaFields, fieldIndex))
      ),
      type: new UntypedFormControl(persona != null && persona.type != null ? persona.type : PersonaTypes.VISITOR),
      options: this.generateFormOptionsFormGroup(persona),
      landing_page_options: new UntypedFormControl(landing_page_options || this.generateLandingPageOptions()),
      pass_code: new UntypedFormControl(persona.pass_code),
      opened: new UntypedFormControl(false),
      products_count: new UntypedFormControl(persona.products_count),
      email_subject: new UntypedFormControl(
        persona.form_email?.email_subject
          ? persona.form_email?.email_subject
          : eventName.length > 66
          ? `Confirmation: ${eventName.substring(0, 63)}...`
          : `Confirmation: ${eventName}`,
        Validators.required
      ),
      email_from_name: new UntypedFormControl(
        persona.form_email?.email_from_name ? persona.form_email?.email_from_name : eventName.length > 80 ? `${eventName.substring(0, 77)}...` : `${eventName}`,
        Validators.required
      ),
      email_reply: new UntypedFormControl(persona.form_email?.email_reply ? persona.form_email?.email_reply : 'no-reply@micetribe.com', Validators.required)
    })
    if (applyGlobalHooks) {
      Object.keys(this._globalValueChangesHooks).forEach((property) => {
        formGroup.get(property).valueChanges.subscribe((value) => {
          this._globalValueChangesHooks[property].forEach((hook) => hook.valueChanged(globalHookPersonasFormArray, formGroup, formGroup.get(property), value))
        })
      })
    }
    return formGroup
  }
  public generateFormGroupForm(form: UntypedFormGroup): UntypedFormGroup {
    const formGroup = new UntypedFormGroup({
      event_id: new UntypedFormControl(form.value.event_id),
      id: new UntypedFormControl(form.value.id),
      slug: new UntypedFormControl(form.value.slug),
      require_approval: new UntypedFormControl(form.value.require_approval),
      name: new UntypedFormControl(form.value.name, Validators.required),
      physical_type_id: new UntypedFormControl(form.value.physical_type_id),
      currency_id: new UntypedFormControl(form.value.currency_id),
      registration_amount: new UntypedFormControl(form.value.registration_amount),
      enable_payment: new UntypedFormControl(form.value.enable_payment),
      sub_locations_ids: new UntypedFormControl(form.value.sub_locations_ids),
      promo_code_ids: new UntypedFormControl(form.value.promo_code_ids),
      promo_codes: new UntypedFormControl(form.value.promo_codes),
      weight: new UntypedFormControl(form.value.weight),
      maximum_registrations: new UntypedFormControl(form.value.maximum_registrations),
      success_message: new UntypedFormControl(form.value.success_message),
      regret_message: new UntypedFormControl(form.value.regret_message),
      form_elements: new UntypedFormArray(
        form.value.form_elements
          .sort((a, b) => (a.weight > b.weight ? 1 : -1))
          .map((personaField: PersonaField, fieldIndex: number, personaFields: PersonaFields) => this.generatePersonaFieldFormGroup(personaField, personaFields, fieldIndex))
      ),
      type: new UntypedFormControl(form.value.type != null ? form.value.type : PersonaTypes.VISITOR),
      options: this.generateFormOptionsFormGroup(form.value),
      landing_page_options: new UntypedFormControl(this.generateLandingPageOptions()),
      pass_code: new UntypedFormControl(form.value.pass_code || null),
      opened: new UntypedFormControl(false),
      whatsapp_communication: this.generateWhatsappCommunicationFormGroup(form.value.whatsapp_communication),
      sms_communication: this.generateSmsCommunicationFormGroup(form.value.sms_communication)
    })
    if (form.value.applyGlobalHooks) {
      Object.keys(this._globalValueChangesHooks).forEach((property) => {
        formGroup.get(property).valueChanges.subscribe((value) => {
          this._globalValueChangesHooks[property].forEach((hook) => hook.valueChanged(form.value.globalHookPersonasFormArray, formGroup, formGroup.get(property), value))
        })
      })
    }
    return formGroup
  }
  /**
   * WhatsApp flags sent with the persona. A missing flag stays off.
   * @param source Saved flags, when the persona already has them
   */
  public generateWhatsappCommunicationFormGroup(source?: Partial<WhatsappCommunication> | null): UntypedFormGroup {
    return new UntypedFormGroup({
      enable_online: new UntypedFormControl(this._isChannelFlagOn(source?.enable_online)),
      enable_onsite: new UntypedFormControl(this._isChannelFlagOn(source?.enable_onsite)),
      enable_reminders: new UntypedFormControl(this._isChannelFlagOn(source?.enable_reminders))
    })
  }

  /**
   * Reads a saved channel flag. The API may send a boolean or 0/1.
   * @param value Stored flag
   */
  private _isChannelFlagOn(value: boolean | number | null | undefined): boolean {
    return value === true || value === 1
  }

  /**
   * Generate form options form group
   * @param persona Persona
   * @returns FormGroup
   */
  generateFormOptionsFormGroup(persona: Persona): UntypedFormGroup {
    const group = new UntypedFormGroup({})
    if (persona.options) {
      Object.entries(persona.options).map(([key, option]) => {
        if (!['reminders', 'barcodeColor', 'languages', 'social_login'].includes(key)) {
          group.addControl(key, new UntypedFormControl(option))
        }
      })
    }
    group.addControl(
      'reminders',
      new UntypedFormGroup({
        schedules: new UntypedFormArray(
          persona?.options?.reminders?.schedules?.map((o: Schedule) => {
            return new UntypedFormGroup({
              type: new UntypedFormControl(o.type),
              value: new UntypedFormControl(o.value),
              done: new UntypedFormControl(o.done || false)
            })
          }) || [
            new UntypedFormGroup({
              type: new UntypedFormControl('hour'),
              value: new UntypedFormControl(8),
              done: new UntypedFormControl(false)
            })
          ]
        ),
        status: new UntypedFormArray(
          persona?.options?.reminders?.status?.map(({ count, date, type, value }) => {
            return new UntypedFormGroup({
              count: new UntypedFormControl(count),
              date: new UntypedFormControl(date),
              type: new UntypedFormControl(type),
              value: new UntypedFormControl(value)
            })
          }) || []
        ),
        // Use 'hasOwnProperty' safely for persona.options.reminders
        enabled:
          persona?.options?.reminders && Object.prototype?.hasOwnProperty?.call(persona.options.reminders, 'enabled')
            ? new UntypedFormControl(persona?.options?.reminders?.enabled)
            : new UntypedFormControl(true)
      })
    )
    group.addControl('barcodeColor', new UntypedFormControl(persona?.options?.barcodeColor || '#000000'))
    group.addControl('languages', new UntypedFormControl(persona?.options?.languages || []))
    group.addControl(
      'social_login',
      new UntypedFormGroup({
        linkedin: new UntypedFormControl(persona?.options?.social_login?.linkedin === true)
      })
    )
    return group
  }

  /**
   * SMS flags and message sent with the persona. A missing flag stays off. A null message uses the default body.
   * @param source Saved SMS settings, when the persona already has them
   */
  public generateSmsCommunicationFormGroup(source?: Partial<SmsCommunication> | null): UntypedFormGroup {
    return new UntypedFormGroup({
      enable_online: new UntypedFormControl(this._isChannelFlagOn(source?.enable_online)),
      enable_onsite: new UntypedFormControl(this._isChannelFlagOn(source?.enable_onsite)),
      message: new UntypedFormControl(source?.message?.replaceAll('{event_shortname}', '{event_slug}'))
    })
  }

  /**
   * Generates the current suitable weight for a persona
   * @param weight number
   * @param index number
   * @param elements WeightAttribute[]
   * @returns number
   */
  public generateWeight(weight: number, index: number, elements: WeightAttribute[]): number {
    return elements.some((o, elementIndex) => o.weight === weight && index !== elementIndex) ? this.generateWeight(weight + 1, index, elements) : weight
  }
  /**
   * Generates personas weights
   * @param elements Personas
   */
  public generatePersonasWeights(elements: Personas) {
    elements
      .filter((element) => element.type === PersonaTypes.VISITOR)
      .forEach((element, index, _elements) => {
        element.weight = index
      })
  }
  /**
   * Returns a suitable persona field name for a persona field based on multiple factors and iterations - recursive
   * @param field PersonaField
   * @param name string
   * @param index number
   * @param elements PersonaFields
   * @param iteration number
   * @returns string
   */
  public generatePersonaFieldName(field: PersonaField, name: string, index: number, elements: PersonaFields, iteration = 0): string {
    switch (field.form_element_type_id) {
      case PersonaFieldTypes.FULLNAME:
        return 'fullname'
      case PersonaFieldTypes.PHONE:
        return 'phone'
      case PersonaFieldTypes.EMAIL:
        return 'email'
      case PersonaFieldTypes.ORGANIZATION:
        return 'organization'
      case PersonaFieldTypes.JOB_TITLE:
        return 'job_title'
      default:
        break
    }
    const currentName = name + (iteration > 0 ? iteration.toString() : '')

    return elements.some((o, elementIndex) => o.name === currentName && index !== elementIndex)
      ? this.generatePersonaFieldName(field, name, index, elements, iteration + 1)
      : currentName
  }
  /**
   * Generates a form group that represents a persona field
   * @param personaField PersonaField
   * @param personaFields PersonaFields
   * @param fieldIndex number
   * @param requiredValidator boolean
   * @param uniqueValidator boolean
   * @returns FormGroup
   */
  generatePersonaFieldFormGroup(personaField: PersonaField, personaFields: PersonaFields, fieldIndex: number, requiredValidator = false, uniqueValidator = false) {
    return new UntypedFormGroup({
      id: new UntypedFormControl(personaField.id),
      form_element_type_id: new UntypedFormControl(personaField.form_element_type_id, Validators.required),
      label: new UntypedFormControl(
        personaField.label,
        personaField.form_element_type_id === PersonaFieldTypes.LINE_SEPARATOR ? [] : [Validators.minLength(this.minLabelLength), Validators.required]
      ),
      weight: new UntypedFormControl(this.generateWeight(personaField.weight, fieldIndex, personaFields)),
      name: new UntypedFormControl(personaField.name, [Validators.required]),
      is_required: new UntypedFormControl(personaField.is_required, requiredValidator ? [Validators.requiredTrue, Validators.required] : Validators.required),
      options: this.generateFieldOptionsFormGroup(personaField),
      is_unique: new UntypedFormControl(personaField.is_unique, uniqueValidator ? [Validators.requiredTrue, Validators.required] : Validators.required),
      is_disable_online: new UntypedFormControl(personaField.is_disable_online == null ? false : personaField.is_disable_online),
      is_disable_onsite: new UntypedFormControl(personaField.is_disable_onsite == null ? false : personaField.is_disable_onsite),
      is_titratable: new UntypedFormControl(personaField.is_titratable != null ? personaField.is_titratable : false),
      form_element_values: new UntypedFormArray(
        personaField.form_element_values
          ? personaField.form_element_values
              .sort((a, b) => (a.weight > b.weight ? 1 : -1))
              .map(
                (personaFieldValue, fieldValueIndex) =>
                  new UntypedFormGroup({
                    id: new UntypedFormControl(personaFieldValue.id),
                    name: new UntypedFormControl(personaFieldValue.name),
                    slug: new UntypedFormControl(personaFieldValue.slug),
                    weight: new UntypedFormControl(this.generateWeight(personaFieldValue.weight, fieldValueIndex, personaField.form_element_values)),
                    options: this.generateFieldValueOptionsFormGroup(personaFieldValue)
                  })
              )
          : []
      )
    })
  }
  generateFieldValueOptionsFormGroup(personaFieldValue: PersonaFieldValue) {
    const options = new UntypedFormGroup({})
    if (personaFieldValue.options) {
      // Add options to the form group
      Object.entries(personaFieldValue.options)
        .filter(([key, value]) => key !== 'translations')
        .forEach(([key, value]) => {
          options.addControl(key, new UntypedFormControl(value))
        })
    }
    // Fill the form group with the translations if they exist
    const translations = new UntypedFormGroup({})
    if (personaFieldValue.options?.translations) {
      Object.entries(personaFieldValue.options.translations).forEach(([key, value]) => {
        translations.addControl(key, new UntypedFormControl(value))
      })
    }
    // Add the translations form group to the options form group
    options.addControl('translations', translations)
    return options
  }
  /**
   * Generates a form group that represents a persona field options
   * @param personaField PersonaField
   * @returns
   */
  private generateFieldOptionsFormGroup(personaField: PersonaField) {
    const options = new UntypedFormGroup({})
    if (personaField.options) {
      // Add options to the form group
      Object.entries(personaField.options)
        .filter(([key, value]) => key !== 'translations')
        .forEach(([key, value]) => {
          options.addControl(key, new UntypedFormControl(value))
        })
    }

    options.addControl('readonly', new UntypedFormControl(personaField.options?.readonly))
    // Fill the form group with the translations if they exist
    const translations = new UntypedFormGroup({})
    if (personaField.options?.translations) {
      Object.entries(personaField.options.translations).forEach(([key, value]) => {
        translations.addControl(key, new UntypedFormControl(value))
      })
    }
    // Add the translations form group to the options form group
    options.addControl('translations', translations)
    return options
  }
  /**
   * Determines whether a persona field supports having multiple select values as options
   * @param fieldType PersonaFieldTypes
   * @returns boolean
   */
  public supportsFieldsValue(fieldType: PersonaFieldTypes) {
    return [PersonaFieldTypes.RADIO, PersonaFieldTypes.AGE_GROUP, PersonaFieldTypes.CHECKBOX, PersonaFieldTypes.GENDER].includes(fieldType)
  }
  /**
   * Builds the communication PATCH body from a persona form value.
   * @param persona Persona form value
   */
  public toCommunicationPayload(persona: Persona): PersonaCommunicationPayload {
    return {
      enable_communication: persona.enable_communication ?? false,
      require_approval: this._isChannelFlagOn(persona.require_approval as boolean | number | null | undefined),
      success_message: persona.success_message ?? null,
      regret_message: persona.regret_message ?? null,
      pending_approval_message: persona.options?.pendingApprovalMessage ?? null,
      whatsapp_communication: persona.whatsapp_communication ?? null,
      sms_communication: persona.sms_communication ?? null,
      mail_communication: {
        email_reply: persona.email_reply ?? null,
        email_subject: persona.email_subject ?? null,
        email_from_name: persona.email_from_name ?? null
      }
    }
  }

  /**
   * Generates multiple observables that determines the api calls that needs to happen to create the current personas in the backend
   * @param personas Personas
   * @param event ClientRepresentedEvent
   * @returns { create: Observable[]; update: Observable[]}
   */
  public toRequest(personas: Personas, event: ClientRepresentedEvent) {
    let request: Personas = null

    // Prepare personas request
    request = personas.map((element: Persona, index: number) => {
      const persona = element

      // Prepare persona fields
      persona.form_elements = persona.form_elements.map((field: PersonaField, fieldIndex: number, fields: PersonaFields) => {
        // Prepare persona fields slugs
        field.name = this.generatePersonaFieldName(field, field.name, fieldIndex, fields)
        // Check and update/ remove (un)supported field values
        if (this.supportsFieldsValue(field.form_element_type_id)) {
          field.form_element_values = field.form_element_values.filter((fieldValue: PersonaFieldValue) => {
            return !!fieldValue.name && !!fieldValue.slug
          })
        } else {
          delete field.form_element_values
        }
        return field
      })
      persona.event_id = event.id
      if (persona.type == null) {
        persona.type = PersonaTypes.VISITOR
      }
      if (!persona.id) {
        delete persona.id
      }
      if (!persona.currency_id || !(this.auth.currentParentWorkspace || this.auth.currentWorkspace).can_collect_payment) {
        delete persona.registration_amount
        delete persona.currency_id
        delete persona.currency
        delete persona.enable_payment
      }
      return persona
    })
    return {
      create: request.filter((persona) => !persona?.id),
      update: request.filter((persona) => !!persona?.id)
    }
  }
}
